import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Label } from "../components/ui/Label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/Select";
import { Textarea } from "../components/ui/Textarea";
import { Switch } from "../components/ui/Switch";
import { DatePicker } from "../components/ui/DatePicker";
import { ArrowLeft, Save, FileText, Upload, Plus, Loader2, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { blogService, Post as BlogPost, Category as BlogCategory, PostStatus } from "../services/blog";
import { toast } from "sonner";
import { RichTextEditor } from "../components/RichTextEditor";
import { getImageUrl, toUtcISOString, formatToDateTimeLocal } from "../utils";

interface NovoPostFormProps {
  onBack: () => void;
  postData?: {
    post: BlogPost;
  };
}

export function NovoPostForm({ onBack }: NovoPostFormProps) {
  const { id } = useParams();
  const isEditing = !!id;

  const [categorias, setCategorias] = useState<BlogCategory[]>([]);
  const [novaCategoria, setNovaCategoria] = useState("");
  const [mostrarNovaCategoriaInput, setMostrarNovaCategoriaInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [formData, setFormData] = useState<Partial<BlogPost>>({
    title: "",
    body: "",
    summary: "",
    status: "PUBLISHED",
    featured: false,
    category_ids: [],
    published_at: formatToDateTimeLocal(new Date().toISOString()),
    image: "",
    meta_title: "",
    meta_description: ""
  });

  useEffect(() => {
    loadCategories();
    if (id) {
      loadPost(id);
    }
  }, [id]);

  const loadPost = async (postId: string) => {
    try {
      setLoading(true);
      const data = await blogService.getAdminPostById(postId);
      const post = data.data || data;
      setFormData({
        ...post,
        category_ids: post.categories?.map((c: any) => c.id) || [],
        published_at: formatToDateTimeLocal(post.published_at) || formatToDateTimeLocal(new Date().toISOString()),
        summary: post.summary || "",
        meta_title: post.meta_title || "",
        meta_description: post.meta_description || ""
      });
    } catch (error) {
      console.error("Erro ao carregar post:", error);
      toast.error("Erro ao carregar dados do post");
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const data = await blogService.getCategories();
      setCategorias(data);
    } catch (error) {
      toast.error("Erro ao carregar categorias");
    } finally {
      setLoadingCategories(false);
    }
  };

  const toggleCategory = (catId: number) => {
    setFormData(prev => {
      const currentIds = prev.category_ids || [];
      const newIds = currentIds.includes(catId)
        ? currentIds.filter(id => id !== catId)
        : [...currentIds, catId];
      return { ...prev, category_ids: newIds };
    });
  };

  const handleAdicionarCategoria = async () => {
    if (novaCategoria.trim()) {
      try {
        const data = await blogService.createCategory({ name: novaCategoria.trim() });
        setCategorias([...categorias, data]);
        const currentIds = formData.category_ids || [];
        setFormData({ ...formData, category_ids: [...currentIds, data.id] });
        setNovaCategoria("");
        setMostrarNovaCategoriaInput(false);
        toast.success("Categoria adicionada");
      } catch (error) {
        toast.error("Erro ao adicionar categoria");
      }
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);

    try {
      const data = await blogService.uploadImage(file);
      setFormData(prev => ({
        ...prev,
        image: data.path || data.location
      }));
      toast.success("Imagem enviada com sucesso!");
    } catch (error) {
      console.error("Erro no upload:", error);
      toast.error("Falha ao enviar a imagem.");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Lógica simples:
      // - DRAFT: rascunho, nunca aparece no site
      // - PUBLISHED: aparece no site SOMENTE se published_at <= agora
      // O frontend filtra por: status = 'PUBLISHED' AND published_at <= NOW()
      // Isso permite agendamento sem cron jobs
      const dataToSave = {
        ...formData,
        category_ids: formData.category_ids || [],
        status: formData.status?.toString().toUpperCase() || 'DRAFT',
        published_at: toUtcISOString(formData.published_at) || formData.published_at,
      };

      if (isEditing && id) {
        await blogService.updatePost(id, dataToSave);
        toast.success("Post atualizado com sucesso!");
      } else {
        await blogService.createPost(dataToSave);
        toast.success("Post criado com sucesso!");
      }
      onBack();
    } catch (error) {
      console.error(error);
      toast.error("Erro ao salvar post");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="flex-1 p-6 bg-gray-50">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={onBack}
          className="mb-4 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070]"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para Blog
        </Button>
        <h1 className="text-2xl font-bold text-[#68686A]">
          {isEditing ? "Editar Post" : "Novo Post"}
        </h1>
        <p className="text-[#68686A]">
          {isEditing ? "Editar artigo existente do blog" : "Criar novo artigo para o blog"}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-6xl space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 space-y-6">
            <Card className="border border-gray-200 hover:shadow-md transition-shadow duration-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#68686A]">
                  <FileText className="h-5 w-5" />
                  Conteúdo Principal
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="titulo" className="text-sm font-medium text-[#68686A]">Título do Post</Label>
                  <Input
                    id="titulo"
                    placeholder="Ex: O Futuro da Moda Sustentável..."
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="border-gray-300 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent"
                  />
                </div>

                <div>
                  <Label htmlFor="summary" className="text-sm font-medium text-[#68686A]">Resumo (Exibição na lista)</Label>
                  <Textarea
                    id="summary"
                    placeholder="Breve resumo do post para exibição na listagem do blog..."
                    value={formData.summary || ""}
                    onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                    className="border-gray-300 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent"
                    rows={3}
                  />
                  <p className="text-xs text-gray-500 mt-1">Este texto será exibido na página inicial do blog. Deixe em branco para gerar automaticamente.</p>
                </div>

                <div>
                  <Label className="text-sm font-medium text-[#68686A]">Conteúdo do Post</Label>
                  <RichTextEditor
                    value={formData.body || ""}
                    onChange={(content) => setFormData({ ...formData, body: content })}
                    folder="blog"
                    placeholder="Escreva seu artigo aqui..."
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border border-gray-200 hover:shadow-md transition-shadow duration-200">
              <CardHeader>
                <CardTitle className="text-[#68686A]">Imagem de Destaque</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {formData.image ? (
                  <div className="space-y-2">
                    <div className="relative">
                      <img
                        src={getImageUrl(formData.image)}
                        alt="Imagem de capa"
                        className="w-full h-40 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image: "" })}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 shadow-md"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 text-center">1200x630px recomendado</p>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center group hover:border-[#D4C070] transition-colors">
                    {uploadingImage ? (
                      <Loader2 className="h-8 w-8 text-[#D4C070] mx-auto animate-spin" />
                    ) : (
                      <>
                        <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2 group-hover:text-[#D4C070] transition-colors" />
                        <p className="text-sm text-[#68686A] mb-3">1200x630px recomendado</p>
                        <Label htmlFor="image-upload" className="cursor-pointer inline-block bg-white border border-gray-300 px-4 py-2 rounded-md text-sm font-medium text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070] transition-colors">
                          Selecionar Imagem
                        </Label>
                        <input
                          id="image-upload"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleImageUpload}
                        />
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border border-gray-200 hover:shadow-md transition-shadow duration-200">
              <CardHeader>
                <CardTitle className="text-[#68686A]">Configurações</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-[#68686A]">Categorias</Label>
                  <div className="space-y-3 mt-2 max-h-48 overflow-y-auto p-3 border rounded-md border-gray-200 bg-white">
                    {loadingCategories ? (
                      <Loader2 className="h-4 w-4 animate-spin mx-auto text-gray-400" />
                    ) : (
                      categorias.map((cat) => (
                        <div key={cat.id} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={`cat-${cat.id}`}
                            checked={formData.category_ids?.includes(cat.id)}
                            onChange={() => toggleCategory(cat.id)}
                            className="h-4 w-4 rounded border-gray-300 text-[#D4C070] focus:ring-[#D4C070]"
                          />
                          <Label
                            htmlFor={`cat-${cat.id}`}
                            className="text-sm text-[#68686A] cursor-pointer"
                          >
                            {cat.name}
                          </Label>
                        </div>
                      ))
                    )}
                  </div>

                  {mostrarNovaCategoriaInput ? (
                    <div className="flex gap-2 mt-2">
                      <Input
                        id="nova-categoria"
                        placeholder="Nome..."
                        value={novaCategoria}
                        onChange={(e) => setNovaCategoria(e.target.value)}
                        className="h-8 border-gray-300"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAdicionarCategoria}
                        className="h-8 shadow-sm"
                      >
                        Adicionar
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setMostrarNovaCategoriaInput(true)}
                      className="w-full text-xs text-[#68686A] mt-1"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Nova Categoria
                    </Button>
                  )}
                </div>

                <div>
                  <Label htmlFor="status" className="text-sm font-medium text-[#68686A]">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value as PostStatus })}
                  >
                    <SelectTrigger className="border-gray-300 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent mt-1 h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={PostStatus.DRAFT}>Rascunho</SelectItem>
                      <SelectItem value={PostStatus.PUBLISHED}>Publicado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="data-publicacao" className="text-sm font-medium text-[#68686A]">Data de Publicação</Label>
                  <div className="mt-1">
                    <DatePicker
                      value={formData.published_at}
                      onChange={(value) => setFormData({ ...formData, published_at: value })}
                      placeholder="Selecione a data"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2 pt-2">
                  <Switch
                    id="featured"
                    checked={formData.featured}
                    onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
                  />
                  <Label htmlFor="featured" className="text-sm text-[#68686A]">Destaque</Label>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-[#68686A]">SEO (Otimização)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="meta_title" className="text-xs text-[#68686A]">Meta Title</Label>
                  <Input
                    id="meta_title"
                    placeholder="Título para o Google..."
                    value={formData.meta_title || ""}
                    onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="meta_description" className="text-xs text-[#68686A]">Meta Description</Label>
                  <Textarea
                    id="meta_description"
                    placeholder="Descrição para o Google..."
                    value={formData.meta_description || ""}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    className="text-sm"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 hover:shadow-md transition-shadow duration-200">
              <CardHeader>
                <CardTitle className="text-[#68686A]">Ações</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 px-4 pb-6">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#D4C070] hover:bg-[#849D70] text-white"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {(() => {
                    const status = formData.status?.toString().toUpperCase();
                    if (status === 'DRAFT') return isEditing ? 'Salvar Rascunho' : 'Salvar Rascunho';
                    return isEditing ? 'Atualizar Post' : 'Publicar Post';
                  })()}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={onBack}
                  className="w-full border-gray-300 text-[#68686A]"
                >
                  Cancelar
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </main>
  );
}

export default NovoPostForm;
